import * as go from 'gojs';

import {
  HiddenPart,
  defaultHiddenPartData,
} from 'src/containers/ProjectDiagram/templates/extensions/hiddenPart';
import { ExtendedForceDirectedLayout } from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/ExtendedForceDirectedLayout';
import { ExtendedLayeredDigraphLayout } from 'src/containers/ProjectDiagram/templates/extensions/layouts/LayeredLayout/ExtendedLayeredDigraphLayout';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';

export type ExtendedDiagram = {
  appendSelectionHistory: boolean;
  commit: (func: (d: ExtendedDiagram) => void, tname?: string | null) => void;
  getPreviousSelection: () => {
    previousIdx: number;
    previousSelection: go.Set<go.Part> | undefined;
  };
  getSelectionHistory: (idx: number) => go.Set<go.Part> | undefined;
  hiddenPart: HiddenPart;
  layout: ExtendedForceDirectedLayout | ExtendedLayeredDigraphLayout;
  model: Model;
  selectionHistory: go.List<go.Set<go.Part>>;
  selectPreviousSelection: () => void;
  updateSelectionHistory: () => void;
} & go.Diagram;

export function createExtendedDiagram(
  init?: go.DiagramInitOptions
): ExtendedDiagram {
  const diagram = new go.Diagram(init) as ExtendedDiagram;

  /**
   * Gets or sets whether the next change to #updateSelectionHistory should
   * append the current selection to the selection history.
   * The initial value is false.
   *
   * @see #selectionHistory
   */
  diagram.appendSelectionHistory = false;

  /**
   * Return the index and value of the previous valid selection. A valid
   * selection is one where all of its parts exist on the diagram or were hidden
   * via @see this.hiddenPart.setModelConcept and the selection is not equal to
   * the current selection.
   *
   * @see #selectionHistory
   */
  function getPreviousSelection(this: ExtendedDiagram) {
    const currentIdx = this.selectionHistory.size - 1;
    const currentSelection = this.getSelectionHistory(currentIdx);
    const currentKeys = currentSelection?.map((part) => part.key);
    let previousIdx = currentIdx - 1;
    let previousSelection = this.getSelectionHistory(previousIdx);

    while (
      previousIdx > -1 &&
      previousSelection &&
      (previousSelection.all((part) => Boolean(currentKeys?.has(part.key))) ||
        !previousSelection.all(
          (part) =>
            part === this.hiddenPart || Boolean(this.findPartForKey(part.key))
        ))
    ) {
      previousIdx -= 1;
      previousSelection = this.getSelectionHistory(previousIdx);
    }

    return { previousIdx, previousSelection };
  }
  diagram.getPreviousSelection = getPreviousSelection.bind(diagram);

  /**
   * Return the selection collection at a given selection history index.
   *
   * @see #selectionHistory
   */
  function getSelectionHistory(this: ExtendedDiagram, idx: number) {
    return idx < 0 ? undefined : this.selectionHistory.get(idx);
  }
  diagram.getSelectionHistory = getSelectionHistory.bind(diagram);

  /**
   * A hidden go.Part that always exists in the diagram to facilitate selection
   * of things that aren't rendered, such as attributes
   */
  diagram.hiddenPart = new HiddenPart({
    data: { ...defaultHiddenPartData },
  });

  /**
   * This read-only property returns the read-only collection of read-only selection collections.
   *
   * Do not modify this collection.
   *
   * @see #selection
   */
  diagram.selectionHistory = new go.List();

  /**
   * Select all of the Parts in the previous selection, and deselect all other Parts.
   * This method raises the "ChangingSelection" and "ChangedSelection" Diagram events.
   *
   * @see #selectionHistory
   * @see #getPreviousSelection
   */
  function selectPreviousSelection(this: ExtendedDiagram) {
    const currentIdx = this.selectionHistory.size - 1;
    const { previousIdx, previousSelection } = this.getPreviousSelection();

    this.hiddenPart.setPreviousValue(previousIdx);
    this.selectionHistory.removeRange(previousIdx, currentIdx);
    setDiagramSelection(previousSelection, true)(this);
  }
  diagram.selectPreviousSelection = selectPreviousSelection.bind(diagram);

  /**
   * Updates the selection history based on the current selection collection.
   * If we are not appending the selection history, clear the current history
   * If the current selection is not empty, add the current selection to the selection history.
   * If the current selction is empty, clear the selection history.
   *
   * @see #selection
   * @see #selectionHistory
   */
  function updateSelectionHistory(this: ExtendedDiagram) {
    if (!this.appendSelectionHistory) {
      this.selectionHistory.clear();
    }

    if (this.selection.size) {
      this.selectionHistory.add(this.selection.copy());
      this.hiddenPart.addHiddenHistory(
        this.selection.has(this.hiddenPart),
        this.appendSelectionHistory
      );
    } else {
      this.selectionHistory.clear();
      this.hiddenPart.clearHiddenHistory();
    }

    this.appendSelectionHistory = false;
  }
  diagram.updateSelectionHistory = updateSelectionHistory.bind(diagram);

  diagram.add(diagram.hiddenPart);

  return diagram;
}
